import { useState, useEffect } from 'react';
import { config } from '../../../config';
import { prepareHeaders } from '../../../configuration/tokenHandling/prepareHeaders';
import { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { IPagination } from '../../../shared/models/IPagination';
import { IFinancialHealth } from '../models/IFinancialHealth';
import { intToMonth } from '../../../shared/utils/formatters';

type UseFinancialHealthDataProps = {
  externalViewType: TableViewTogglesViewType;
  assetId: string | undefined;
  offset: number;
};

interface IApiResponse {
  items: IFinancialHealthItem[],
  pagination: IPagination
}

interface IFinancialHealthItem {
  month: number,
  year: number,
  monthScore: number,
  averageScore: number,
  activeMaintenanceContract: number,
  paymentOnTime: number,
  responsibleMaintenance: number,
  brandLoyalty: number,
  [key: string]: any;
}

export const useFinancialHealthData = ({
assetId, offset}: UseFinancialHealthDataProps) => {
  const [ financialHealthResponseList, setFinancialHealthResponseList ] = useState<IFinancialHealth[]>([]);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ error, setError ] = useState(null);
  const [pagination, setPagination] = useState<IPagination>();

  useEffect(() => {

    const getFinancialHealth = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = await prepareHeaders(new Headers());
        const response = await fetch(config.backend.SCORES_SERVICE + `/v2/relationships/${assetId}/financial-health?offset=${offset}&size=50`, {
          method: 'GET',
          headers
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = (await response.json()) as IApiResponse;

        setPagination(data.pagination);

        setFinancialHealthResponseList(data.items.map((item: IFinancialHealthItem) => {
          const result: IFinancialHealth = {
            date: `${intToMonth(item.month)}/${item.year}`,
            year: item.year,
            month: item.month,
            score: item.monthScore,
            averageScore: item.averageScore,
            activeMaintenanceContract: item.activeMaintenanceContract,
            brandLoyalty: item.brandLoyalty,
            paymentOnDay: item.paymentOnTime,
            responsibleMaintenance: item.responsibleMaintenance
          }
          return result;
        }));

      } catch (e: any) {
        console.error('Error fetching relationship:', e);
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };
    if(assetId){
      getFinancialHealth();
    }
  }, [offset, assetId]);

  return {
    financialHealthResponseList,
    pagination,
    setPagination,
    isLoading,
    error,
  };
};